import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { neutral, palette } from '@ahora-patagonia/design-tokens';
import { EspacioPublicitario } from '../../../shared/ads/EspacioPublicitario.js';
import { TiraLateral } from '../../../shared/lateral/TiraLateral.js';
import { SECCION_KIND } from '../../../seccion/models/entities/SeccionData.js';
import { ListadoNotas } from '../../../shared/notas/ListadoNotas.js';
import { useObservable } from '../../../shared/hooks/useObservable.js';
import { useViewModel } from '../../../shared/hooks/useViewModel.js';
import { useSincronizarPagina } from '../../../shared/shell/paginaPublica.js';
import { useSenalDePagina } from '../../../shared/senales/useSenalDePagina.js';
import { useHead } from '../../../shared/seo/useHead.js';
import { useSitio, useSsrData } from '../../../shared/ssr/SsrDataProvider.js';
import { NotFoundView } from '../../../shared/views/NotFoundView.js';
import type { NotFoundData } from '../../../notfound/notfound.loader.js';
import { headDeCategoria } from '../../categoria.head.js';
import { CATEGORIA_ROUTE_KEY } from '../../categoria.loader.js';
import type { CategoriaData } from '../../models/entities/CategoriaData.js';
import { CategoriaViewModel } from '../../viewmodels/CategoriaViewModel.js';

/**
 * La página de sección: cabecera con nombre y descripción, y un listado **uniforme** de 12
 * notas con "Ver más" de 12 en 12 — sin bajada y sin nota destacada, porque un listado es
 * un índice cronológico y la jerarquía es cosa de la portada. La sección vacía existe, con
 * su estado vacío.
 */
export function CategoriaView() {
  const params = useParams();
  const urlKey = params['categoria'] ?? '';
  const sitio = useSitio();
  const initial = useSsrData<CategoriaData | NotFoundData>(CATEGORIA_ROUTE_KEY);
  const esCategoria = initial !== null && 'category' in initial;
  const vm = useViewModel(() => new CategoriaViewModel(esCategoria ? initial : null, urlKey));
  const state = useObservable(vm.state$);
  /** La sección que resolvió el server. Los datos del SSR valen solo para esa. */
  const urlKeyDelSsr = useRef(urlKey).current;

  // Depende de `urlKey`: del menú se salta de una sección a otra por la misma ruta, así que
  // la vista no se remonta y un efecto con `[vm]` no se volvía a disparar.
  useEffect(() => {
    void vm.irA(urlKey);
  }, [vm, urlKey]);
  useSincronizarPagina(state.data);
  useSenalDePagina('SECCION', state.data?.category.id, state.data?.category.id);
  // Saltando de una sección a otra no corre ningún loader: el `<head>` lo reaplica la vista
  // con el mismo armador que usa el server.
  useHead(
    useMemo(
      () => (state.data ? headDeCategoria(state.data.category, sitio) : null),
      [state.data, sitio],
    ),
  );

  // El 404 del server vale mientras siga en esa sección; si desde ahí se elige otra del
  // menú, hay que mostrarla.
  if (urlKeyDelSsr === urlKey && initial !== null && !esCategoria) {
    return <NotFoundView data={initial} />;
  }
  if (state.noEncontrada) return <NotFoundView />;

  const data = state.data;
  if (!data) {
    return (
      <Box sx={{ py: 2 }}>
        {state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <>
            <Skeleton height={48} width="40%" />
            <Skeleton variant="rectangular" height={96} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={96} sx={{ mt: 1 }} />
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      <EspacioPublicitario ubicacion="HEADER" ad={data.ads.HEADER} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
          gap: { xs: 0, lg: 4 },
          alignItems: 'stretch',
          mt: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ borderBottom: `2px solid ${palette.ink}`, pb: 1 }}>
            <Typography variant="tituloNota" component="h1">
              {data.category.name}
            </Typography>
            {data.category.description && (
              <Typography variant="bajada" component="p" sx={{ color: neutral[700], mt: 0.5 }}>
                {data.category.description}
              </Typography>
            )}
          </Box>

          {state.items.length === 0 ? (
            <Typography variant="bajada" component="p" sx={{ color: neutral[700], py: 4 }}>
              Todavía no hay notas publicadas en esta sección. Las últimas del medio están
              en la portada.
            </Typography>
          ) : (
            <>
              <ListadoNotas notas={state.items} />
              {state.items.length < data.total && (
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={state.cargandoMas}
                  onClick={() => void vm.verMas()}
                  sx={{ mt: 2 }}
                >
                  {state.cargandoMas ? 'Cargando…' : 'Ver más'}
                </Button>
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <TiraLateral
            notas={data.ultimaHora}
            titulo={data.shell.ultimaHoraNombre}
            verTodo={`/${SECCION_KIND.ultimaHora}`}
            ad={data.ads.SIDEBAR}
          />
        </Box>
      </Box>
    </>
  );
}
